import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Typography, IconButton, Divider, Chip, Paper, MenuItem,
  TextField, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { MapContainer, TileLayer, Popup, CircleMarker, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STAGE_COLORS = {
  identified:   '#78909C',
  qualified:    '#1565C0',
  demo:         '#00897B',
  sandbox:      '#F57C00',
  proposal:     '#7B1FA2',
  negotiation:  '#E65100',
  pilot:        '#3949AB',
  won:          '#2E7D32',
  lost:         '#C62828',
};

const STAGE_LABELS = {
  identified: 'Identifierad', qualified: 'Kvalificerad',
  demo: 'Demo', sandbox: 'Sandbox', proposal: 'Offert',
  negotiation: 'Förhandling', pilot: 'Pilot', won: 'Vunnen', lost: 'Förlorad',
};

const ACTIVE_STAGES = ['identified', 'qualified', 'demo', 'sandbox', 'proposal', 'negotiation', 'pilot'];

// Geocode an address via Nominatim (same as CRMCompany)
async function geocode(address, city) {
  if (!city && !address) return null;
  try {
    const q = encodeURIComponent(`${address || ''} ${city || ''}`.trim());
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${q}&countrycodes=se&format=json&limit=1`,
      { headers: { 'Accept-Language': 'sv' } }
    );
    const data = await res.json();
    if (data.length > 0) return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
  } catch (e) { /* ignore */ }
  return null;
}

export default function AffärerKarta() {
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | won
  const [filterDept, setFilterDept] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [geoJson, setGeoJson] = useState(null);
  const [allLeads, setAllLeads] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [leadsSnap, companiesSnap, deptsSnap, productsSnap] = await Promise.all([
        getDocs(query(collection(db, 'crmLeads'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'crmCompanies')),
        getDocs(collection(db, 'departments')),
        getDocs(query(collection(db, 'products'), orderBy('name'))),
      ]);

      const companies = {};
      companiesSnap.docs.forEach(d => { companies[d.id] = { id: d.id, ...d.data() }; });
      setDepartments(deptsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProducts(productsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const leads = leadsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllLeads(leads);
      setLoading(false);

      // Hämta GeoJSON för kommuner (en gång, cachelagrat i state)
      try {
        const geoRes = await fetch(
          'https://raw.githubusercontent.com/okfse/sweden-geojson/master/swedish_municipalities.geojson'
        );
        if (geoRes.ok) setGeoJson(await geoRes.json());
      } catch (e) { /* karta fungerar ändå utan kommunlager */ }

      // Geocode companies that have addresses
      setGeocoding(true);
      const geocacheKey = 'affarer_geocache';
      let cache = {};
      try { cache = JSON.parse(localStorage.getItem(geocacheKey) || '{}'); } catch (e) { /**/ }

      const result = [];
      const noCoords = [];
      const seenCompanies = new Set();

      for (const lead of leads) {
        const company = lead.companyId ? companies[lead.companyId] : null;
        const cacheKey = company?.id || (lead.title + lead.status);

        if (company && !seenCompanies.has(company.id)) {
          seenCompanies.add(company.id);
        }

        let coords = cache[cacheKey] || null;
        if (!coords && company && (company.address || company.city)) {
          coords = await geocode(company.address, company.city);
          if (coords) {
            cache[cacheKey] = coords;
          }
          // Small delay to avoid Nominatim rate limiting
          await new Promise(r => setTimeout(r, 300));
        }

        if (coords) {
          // Add slight jitter if same company has multiple leads
          const jitter = () => (Math.random() - 0.5) * 0.005;
          result.push({
            id: lead.id,
            lat: coords.lat + jitter(),
            lon: coords.lon + jitter(),
            leadTitle: lead.title,
            companyName: company?.name || '',
            status: lead.status,
            estimatedValue: lead.estimatedValue,
            departmentIds: lead.departmentIds || [],
            productId: lead.productId || '',
            city: company?.city || '',
          });
        } else {
          noCoords.push({
            id: lead.id,
            leadTitle: lead.title,
            companyName: company?.name || '',
            status: lead.status,
            estimatedValue: lead.estimatedValue,
            departmentIds: lead.departmentIds || [],
            productId: lead.productId || '',
          });
        }
      }
      setUnmapped(noCoords);

      try { localStorage.setItem(geocacheKey, JSON.stringify(cache)); } catch (e) { /**/ }
      setMarkers(result);
      setGeocoding(false);
    }
    load();
  }, []);

  const filtered = markers.filter(m => {
    if (filterStatus === 'active' && !ACTIVE_STAGES.includes(m.status)) return false;
    if (filterStatus === 'won' && m.status !== 'won') return false;
    if (filterDept && !m.departmentIds.includes(filterDept)) return false;
    if (filterProduct && m.productId !== filterProduct) return false;
    return true;
  });

  // Stats
  const activeCount = filtered.filter(m => ACTIVE_STAGES.includes(m.status)).length;
  const wonCount = filtered.filter(m => m.status === 'won').length;
  const totalValue = filtered
    .filter(m => ACTIVE_STAGES.includes(m.status))
    .reduce((s, m) => s + (m.estimatedValue || 0), 0);

  const formatSEK = v => v ? Number(v).toLocaleString('sv-SE') + ' KSEK' : '–';

  // Aggregera leads per kommun (respekterar filter)
  const muniStats = {};
  allLeads.forEach(lead => {
    if (!lead.municipality) return;
    if (filterStatus === 'active' && !ACTIVE_STAGES.includes(lead.status)) return;
    if (filterStatus === 'won' && lead.status !== 'won') return;
    if (filterDept && !(lead.departmentIds || []).includes(filterDept)) return;
    if (filterProduct && lead.productId !== filterProduct) return;
    const key = lead.municipality.toLowerCase().trim();
    if (!muniStats[key]) muniStats[key] = { won: 0, active: 0, other: 0, value: 0 };
    if (lead.status === 'won') muniStats[key].won++;
    else if (ACTIVE_STAGES.includes(lead.status)) muniStats[key].active++;
    else muniStats[key].other++;
    muniStats[key].value += lead.estimatedValue || 0;
  });

  function getMuniStyle(feature) {
    const name = (feature.properties.name || '').toLowerCase().trim();
    const s = muniStats[name];
    if (!s) return { fillColor: '#e8e8e8', weight: 0.5, color: '#ccc', fillOpacity: 0.15 };
    if (s.won > 0 && s.active === 0)
      return { fillColor: '#2E7D32', weight: 1, color: '#1B5E20', fillOpacity: Math.min(0.4 + s.won * 0.15, 0.85) };
    if (s.won > 0)
      return { fillColor: '#66BB6A', weight: 1, color: '#388E3C', fillOpacity: Math.min(0.45 + s.won * 0.1, 0.8) };
    if (s.active >= 3)
      return { fillColor: '#FF6B00', weight: 1, color: '#E65100', fillOpacity: Math.min(0.4 + s.active * 0.05, 0.75) };
    if (s.active > 0)
      return { fillColor: '#1565C0', weight: 1, color: '#0D47A1', fillOpacity: Math.min(0.35 + s.active * 0.1, 0.7) };
    return { fillColor: '#78909C', weight: 0.5, color: '#546E7A', fillOpacity: 0.25 };
  }

  const geoJsonKey = filterStatus + filterDept + filterProduct + (geoJson ? '1' : '0');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Affärer – Karta</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              Geografisk utbredning av leads och affärer
            </Typography>
          </Box>
        </Box>
        {geocoding && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} sx={{ color: '#FF6B00' }} />
            <Typography variant="caption" sx={{ color: '#aaa' }}>Geocodar adresser...</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'all',    label: 'Alla leads' },
            { key: 'active', label: 'Aktiva' },
            { key: 'won',    label: '🏆 Vunna' },
          ].map(f => (
            <Chip key={f.key} label={f.label} size="small"
              onClick={() => setFilterStatus(f.key)}
              variant={filterStatus === f.key ? 'filled' : 'outlined'}
              sx={{ fontWeight: filterStatus === f.key ? 700 : 400,
                bgcolor: filterStatus === f.key ? '#1a1a1a' : 'transparent',
                color: filterStatus === f.key ? 'white' : 'text.primary' }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <TextField select size="small" value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }} label="Affärsområde">
            <MenuItem value="">Alla affärsområden</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={filterProduct}
            onChange={e => setFilterProduct(e.target.value)}
            sx={{ minWidth: 180, bgcolor: 'white', borderRadius: 1 }} label="Produkt">
            <MenuItem value="">Alla produkter</MenuItem>
            {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
        </Box>

        {/* Stats bar */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            { label: `${filtered.length} på kartan`, color: '#1a1a1a' },
            { label: `${activeCount} aktiva`, color: '#1565C0' },
            { label: `${wonCount} vunna`, color: '#2E7D32' },
            { label: `${formatSEK(totalValue)} i aktiv pipeline`, color: '#FF6B00' },
          ].map((s, i) => (
            <Paper key={i} sx={{ px: 2, py: 1, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ color: s.color }}>
                {s.label}
              </Typography>
            </Paper>
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, flex: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Map */}
            <Box sx={{ flex: 1, minWidth: 500, height: 600, borderRadius: 2, overflow: 'hidden' }}>
              <MapContainer
                center={[62.5, 16.5]}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {geoJson && (
                  <GeoJSON
                    key={geoJsonKey}
                    data={geoJson}
                    style={getMuniStyle}
                    onEachFeature={(feature, layer) => {
                      const name = feature.properties.name || '';
                      const s = muniStats[name.toLowerCase().trim()];
                      if (s) {
                        layer.bindTooltip(
                          `<strong>${name}</strong><br/>` +
                          (s.won ? `✅ ${s.won} vunna&nbsp;` : '') +
                          (s.active ? `🔵 ${s.active} aktiva&nbsp;` : '') +
                          (s.value ? `· ${s.value.toLocaleString('sv-SE')} KSEK` : ''),
                          { sticky: true, className: 'leaflet-muni-tooltip' }
                        );
                      }
                    }}
                  />
                )}
                {filtered.map(m => (
                  <CircleMarker
                    key={m.id}
                    center={[m.lat, m.lon]}
                    radius={m.status === 'won' ? 14 : 10}
                    pathOptions={{
                      color: 'white',
                      weight: 2,
                      fillColor: STAGE_COLORS[m.status] || '#78909C',
                      fillOpacity: 0.9,
                    }}
                    eventHandlers={{ click: () => navigate(`/crm/leads/${m.id}`) }}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 180 }}>
                        <Typography variant="body2" fontWeight="bold">{m.leadTitle}</Typography>
                        {m.companyName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {m.companyName}
                          </Typography>
                        )}
                        {m.city && (
                          <Typography variant="caption" display="block" color="text.disabled">
                            📍 {m.city}
                          </Typography>
                        )}
                        <Box sx={{ mt: 0.5 }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '1px 8px',
                            borderRadius: 10,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            backgroundColor: (STAGE_COLORS[m.status] || '#ccc') + '33',
                            color: STAGE_COLORS[m.status] || '#555',
                          }}>
                            {STAGE_LABELS[m.status] || m.status}
                          </span>
                        </Box>
                        {m.estimatedValue > 0 && (
                          <Typography variant="caption" display="block"
                            sx={{ color: '#2E7D32', fontWeight: 600, mt: 0.3 }}>
                            {formatSEK(m.estimatedValue)}
                          </Typography>
                        )}
                        <Typography variant="caption"
                          sx={{ color: '#1565C0', cursor: 'pointer', mt: 0.5, display: 'block' }}
                          onClick={() => navigate(`/crm/leads/${m.id}`)}>
                          Öppna lead →
                        </Typography>
                      </Box>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ width: 220, flexShrink: 0 }}>
              <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                  Kommunfärger
                </Typography>
                {[
                  { color: '#2E7D32', label: 'Vunna affärer' },
                  { color: '#66BB6A', label: 'Vunna + aktiva' },
                  { color: '#FF6B00', label: '3+ aktiva leads' },
                  { color: '#1565C0', label: 'Aktiva leads' },
                  { color: '#78909C', label: 'Övriga leads' },
                  { color: '#e8e8e8', label: 'Inga leads', border: true },
                ].map(({ color, label, border }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.7 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: 1, bgcolor: color,
                      border: border ? '1px solid #ccc' : 'none', flexShrink: 0 }} />
                    <Typography variant="caption">{label}</Typography>
                  </Box>
                ))}
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                  Pins – pipeline-steg
                </Typography>
                {Object.entries(STAGE_COLORS).map(([key, color]) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: '50%',
                      bgcolor: color, border: '2px solid white',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                      flexShrink: 0,
                    }} />
                    <Typography variant="caption">{STAGE_LABELS[key]}</Typography>
                  </Box>
                ))}
              </Paper>

              {filtered.length === 0 && !loading && (
                <Paper sx={{ p: 2, borderRadius: 2, mt: 2, bgcolor: '#fff8f0' }}>
                  <Typography variant="body2" color="text.secondary">
                    Inga leads på kartan. Leads visas när deras företag har adress/ort ifyllt.
                  </Typography>
                </Paper>
              )}

              {/* Leads utan adress */}
              {unmapped.filter(m => {
                if (filterStatus === 'active' && !ACTIVE_STAGES.includes(m.status)) return false;
                if (filterStatus === 'won' && m.status !== 'won') return false;
                if (filterDept && !m.departmentIds.includes(filterDept)) return false;
                if (filterProduct && m.productId !== filterProduct) return false;
                return true;
              }).length > 0 && (
                <Paper sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                    Saknar adress ({unmapped.filter(m => {
                      if (filterStatus === 'active' && !ACTIVE_STAGES.includes(m.status)) return false;
                      if (filterStatus === 'won' && m.status !== 'won') return false;
                      return true;
                    }).length})
                  </Typography>
                  {unmapped.filter(m => {
                    if (filterStatus === 'active' && !ACTIVE_STAGES.includes(m.status)) return false;
                    if (filterStatus === 'won' && m.status !== 'won') return false;
                    if (filterDept && !m.departmentIds.includes(filterDept)) return false;
                    if (filterProduct && m.productId !== filterProduct) return false;
                    return true;
                  }).map(m => (
                    <Box key={m.id}
                      onClick={() => navigate(`/crm/leads/${m.id}`)}
                      sx={{ cursor: 'pointer', py: 0.6, borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 0 },
                        '&:hover': { color: '#1565C0' } }}>
                      <Typography variant="caption" display="block" fontWeight={600} lineHeight={1.3}>
                        {m.leadTitle}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.2 }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%',
                          bgcolor: STAGE_COLORS[m.status] || '#ccc', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                          {STAGE_LABELS[m.status] || m.status}
                          {m.estimatedValue > 0 && ` · ${formatSEK(m.estimatedValue)}`}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block', fontSize: '0.65rem' }}>
                    Lägg till adress på företaget för att visa på kartan
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
